import ModalHeader from 'features/shared/components/modal/ModalHeader';
import ModalFooter from 'features/shared/components/modal/ModalFooter';
import React, { useRef } from 'react';
import { Modal } from 'react-bootstrap';
import FormGroup from 'features/shared/forms/components/FormGroup';
import { useForm } from 'features/shared/forms/hooks/useForm';
import FileInput from 'features/shared/forms/components/FileInput';
import DocumentTypeEnumDTO from 'features/shared/enums/dynamic/DocumentTypeEnumDTO';
import RequirementReactionDTO from '../../dtos/RequirementReactionDTO';
import DocumentSimplifiedDTO from 'features/invoices/dtos/DocumentSimplifiedDTO';
import { RequirementDetail_InsertRequirementReactionDialog } from 'features/shared/components/loaders/promiseLoadingAreas';
import PromiseLoadingSpinner from 'features/shared/components/loaders/PromiseLoadingSpinner';
import { useSelector } from 'react-redux';
import IAppState from 'infrastructure/store/IAppState';
import CustomerState from 'infrastructure/store/customer/CustomerState';
import { insertReactionValidaitons } from './insertReactionValidaitons';
import RolesChecker from 'features/customer/components/RolesChecker';
import { useTranslation } from 'react-i18next';

interface Props {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmit: (reaction: RequirementReactionDTO, files: FileList | null) => void;
  documentTypes: DocumentTypeEnumDTO[];
}

const InsertRequirementReactionDialog = ({ show, setShow, onSubmit, documentTypes }: Props) => {
  // translations
  const { t } = useTranslation("shared\\validations"); 
  const tLabels = useTranslation("requirements\\requirements").t;

  // refs
  const fileInputEl = useRef<HTMLInputElement>(null);

  // store
  const customer = (useSelector<IAppState, CustomerState>((store) => store.customer)).customer;

  // form hook
  const { values, errors, bindField, bindUncontrolledField, valideteForm, clearForm } = useForm({initialValidations: insertReactionValidaitons(fileInputEl, t)}) ;

  // handlers
  const submitFormHandler = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (valideteForm()) {
      let reaction: RequirementReactionDTO = {
        reactionHtml: values["reactionHtml"],
        documents: processDocuments(values["files"], values["documentsType"]),
        customerId: customer.customerId
      };

      onSubmit(reaction, values["files"]);    
      clearForm();  
    }

  };

  const closeDialogHandler = () => {
    clearForm();
    setShow(false);
  }

  // helpers
  const processDocuments = (files: FileList | null, documentType: string) => {
    if (files && files.length > 0) {
      let documents: DocumentSimplifiedDTO[] = [];
      for (const file of files) {
        documents.push({name: file.name, documentType: documentType});
      }

      return documents;
    }
  }

  // render
  return (
    <RolesChecker enabledRoles="Contributor">
      <Modal show={show} onHide={closeDialogHandler} backdrop="static">
        <ModalHeader title={tLabels("insertRequirementReactionDialog.cardTitle")} onCloseClick={closeDialogHandler} />
        <form onSubmit={submitFormHandler}>
          <div className="modal-body py-4">
            <FormGroup labelText={tLabels("insertRequirementReactionDialog.reaction")} inputId="reactionHtml" validationError={errors["reactionHtml"]}>
              <textarea id="reactionHtml" className="form-control" rows={5} placeholder={tLabels("insertRequirementReactionDialog.reactionContent")} {...bindField("reactionHtml")} />
            </FormGroup>

            <FormGroup labelText={tLabels("insertRequirementReactionDialog.documents")} inputId="files" validationError={errors["files"]}>
              <FileInput ref={fileInputEl} id="files" multiple {...bindUncontrolledField("files")} defaultText={tLabels("insertRequirementReactionDialog.setDocument")} />
            </FormGroup>

            <FormGroup labelText={tLabels("insertRequirementReactionDialog.documentType")} inputId="documentsType" validationError={errors["documentsType"]}>
              <select id="documentsType" className="custom-select" {...bindField('documentsType')}>
                <option value="">{tLabels("insertRequirementReactionDialog.setTypeDocument")}</option>
                {documentTypes.map((documentType) => (
                  <option value={documentType.documentTypeCode}>
                    {documentType.documentTypeName}
                  </option>
                ))}
              </select>
            </FormGroup>
          </div>
          <ModalFooter>
            <button type="submit" className="btn btn-primary">{tLabels("insertRequirementReactionDialog.send")}</button>
          </ModalFooter>
        </form>
        <PromiseLoadingSpinner area={RequirementDetail_InsertRequirementReactionDialog} />
      </Modal>
    </RolesChecker>
  )
}

export default InsertRequirementReactionDialog;
